import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Trophy, Target, Handshake, Pencil, Scale, GraduationCap, BarChart3 } from "lucide-react";
import type { Bot } from "@shared/schema";

interface BotStatsResponse {
  rating: number;
  wins: number;
  losses: number;
  winPoints: number;
  lossPoints: number;
  totalMatches: number;
  tournamentCount: number;
}

const STYLE_MAP: Record<string, { label: string; color: string }> = {
  aggressive: { label: "Агрессивный", color: "text-red-400" },
  cooperative: { label: "Кооперативный", color: "text-green-400" },
  balanced: { label: "Сбалансированный", color: "text-blue-400" },
  manipulative: { label: "Манипулятивный", color: "text-purple-400" },
  analytical: { label: "Аналитический", color: "text-cyan-400" },
};

const AVATARS = ["🤖", "🔥", "🤝", "🧕", "📊", "🎭", "⚔️", "🛡️", "🎯", "💼", "🧠", "🦊", "🐺", "🦁", "🐍"];

type LlmProviderChoice = "anthropic" | "yandex";

interface BotFormData {
  name: string; description: string; strategy: string; style: string; avatar: string;
  club: string;
  llmProvider: LlmProviderChoice;
  isJudge: boolean; isTrainer: boolean; judgePrompt: string; trainerPrompt: string;
}

function emptyForm(): BotFormData {
  return { name: "", description: "", strategy: "", style: "balanced", avatar: "🤖", club: "", llmProvider: "anthropic",
    isJudge: false, isTrainer: false, judgePrompt: "", trainerPrompt: "" };
}

function BotFormFields({ f, setF }: { f: BotFormData; setF: (v: BotFormData) => void }) {
  return (
    <div className="space-y-4 mt-2">
      <div className="flex items-center gap-3">
        <div>
          <Label className="text-xs mb-1 block">Аватар</Label>
          <Select value={f.avatar} onValueChange={v => setF({ ...f, avatar: v })}>
            <SelectTrigger className="w-16 text-lg"><SelectValue /></SelectTrigger>
            <SelectContent>{AVATARS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-xs mb-1 block">Имя</Label>
          <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Жёсткий Торговец" data-testid="input-bot-name" />
        </div>
      </div>
      <div>
        <Label className="text-xs mb-1 block">Стиль</Label>
        <Select value={f.style} onValueChange={v => setF({ ...f, style: v })}>
          <SelectTrigger data-testid="select-bot-style"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(STYLE_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs mb-1 block">Модель GPT</Label>
        <Select value={f.llmProvider} onValueChange={v => setF({ ...f, llmProvider: v as LlmProviderChoice })}>
          <SelectTrigger data-testid="select-bot-llm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
            <SelectItem value="yandex">Yandex GPT</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground mt-1">Для Yandex задайте ключи в .env на сервере (см. README).</p>
      </div>
      <div>
        <Label className="text-xs mb-1 block">Описание</Label>
        <Textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })}
          placeholder="Краткое описание бота" rows={2} data-testid="input-bot-description" />
      </div>
      <div>
        <Label className="text-xs mb-1 block">Клуб <span className="text-muted-foreground font-normal">(необязательно)</span></Label>
        <Input value={f.club} onChange={e => setF({ ...f, club: e.target.value })}
          placeholder="Например, Томский клуб переговорщиков" data-testid="input-bot-club" />
      </div>
      <div>
        <Label className="text-xs mb-1 block">Стратегия (системный промпт)</Label>
        <Textarea value={f.strategy} onChange={e => setF({ ...f, strategy: e.target.value })}
          placeholder="Как бот ведёт переговоры..." rows={4} data-testid="input-bot-strategy" />
      </div>

      {/* Judge / Trainer roles */}
      <div className="border-t border-border/30 pt-3 space-y-3">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Роли бота</Label>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={f.isJudge} onCheckedChange={v => setF({ ...f, isJudge: !!v })} />
            <Scale className="w-3.5 h-3.5 text-amber-400" /> Судья
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={f.isTrainer} onCheckedChange={v => setF({ ...f, isTrainer: !!v })} />
            <GraduationCap className="w-3.5 h-3.5 text-green-400" /> Тренер
          </label>
        </div>
        {f.isJudge && (
          <div>
            <Label className="text-xs mb-1 block">Правила судейства (промпт для бота-судьи)</Label>
            <Textarea value={f.judgePrompt} onChange={e => setF({ ...f, judgePrompt: e.target.value })}
              placeholder="Опишите критерии оценки: аргументация, достижение цели, коммуникативная эффективность..."
              rows={3} data-testid="input-bot-judge-prompt" />
          </div>
        )}
        {f.isTrainer && (
          <div>
            <Label className="text-xs mb-1 block">Правила тренерских комментариев (промпт)</Label>
            <Textarea value={f.trainerPrompt} onChange={e => setF({ ...f, trainerPrompt: e.target.value })}
              placeholder="Опишите стиль тренерских комментариев: на что обращать внимание, как давать обратную связь..."
              rows={3} data-testid="input-bot-trainer-prompt" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function BotsPage() {
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editBot, setEditBot] = useState<(Bot & { isJudge?: boolean; isTrainer?: boolean; judgePrompt?: string; trainerPrompt?: string; llmProvider?: string }) | null>(null);
  const [createForm, setCreateForm] = useState<BotFormData>(emptyForm());
  const [editForm, setEditForm] = useState<BotFormData>(emptyForm());
  const [statsBot, setStatsBot] = useState<(Bot & { isJudge?: boolean; isTrainer?: boolean; llmProvider?: string }) | null>(null);

  const { data: bots = [], isLoading } = useQuery<(Bot & { isJudge?: boolean; isTrainer?: boolean; llmProvider?: string })[]>({ queryKey: ["/api/bots"] });

  const { data: botStats, isLoading: statsLoading } = useQuery<BotStatsResponse>({
    queryKey: ["/api/bots", statsBot?.id, "stats"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/bots/${statsBot!.id}/stats`);
      return res.json();
    },
    enabled: !!statsBot,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/bots", createForm);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bots"] });
      setCreateOpen(false);
      setCreateForm(emptyForm());
      toast({ title: "Бот создан" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editBot) return;
      const res = await apiRequest("PUT", `/api/bots/${editBot.id}`, editForm);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bots"] });
      setEditBot(null);
      toast({ title: "Бот обновлён" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => { await apiRequest("DELETE", `/api/bots/${id}`); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bots"] });
      toast({ title: "Бот удалён" });
    },
  });

  const openEdit = (bot: any) => {
    setEditBot(bot);
    setEditForm({
      name: bot.name, description: bot.description, strategy: bot.strategy,
      style: bot.style, avatar: bot.avatar, club: typeof bot.club === "string" ? bot.club : "",
      llmProvider: bot.llmProvider === "yandex" ? "yandex" : "anthropic",
      isJudge: !!bot.isJudge, isTrainer: !!bot.isTrainer,
      judgePrompt: bot.judgePrompt || "", trainerPrompt: bot.trainerPrompt || "",
    });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold" data-testid="text-bots-title">Боты-переговорщики</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Создавайте ботов с настраиваемыми стратегиями, ролями судьи и тренера
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-2" data-testid="button-create-bot">
              <Plus className="w-4 h-4" /> Создать бота
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Новый бот-переговорщик</DialogTitle></DialogHeader>
            <BotFormFields f={createForm} setF={setCreateForm} />
            <Button className="w-full" disabled={!createForm.name || !createForm.strategy || !createForm.description || createMutation.isPending}
              onClick={() => createMutation.mutate()} data-testid="button-save-bot">
              {createMutation.isPending ? "Создаю..." : "Создать бота"}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editBot} onOpenChange={(open) => { if (!open) setEditBot(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Редактирование бота</DialogTitle></DialogHeader>
          <BotFormFields f={editForm} setF={setEditForm} />
          <Button className="w-full" disabled={!editForm.name || !editForm.strategy || !editForm.description || updateMutation.isPending}
            onClick={() => updateMutation.mutate()} data-testid="button-update-bot">
            {updateMutation.isPending ? "Сохраняю..." : "Сохранить изменения"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!statsBot} onOpenChange={(open) => { if (!open) setStatsBot(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Статистика{statsBot ? ` — ${statsBot.name}` : ""}</DialogTitle>
          </DialogHeader>
          {statsLoading ? (
            <p className="text-sm text-muted-foreground">Загрузка…</p>
          ) : botStats ? (
            <dl className="grid gap-3 text-sm">
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Рейтинг (ELO)</dt><dd className="font-medium tabular-nums">{botStats.rating}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Победы</dt><dd className="font-medium tabular-nums">{botStats.wins}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Поражения</dt><dd className="font-medium tabular-nums">{botStats.losses}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Очки в выигранных матчах</dt><dd className="font-medium tabular-nums">{botStats.winPoints}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Очки в проигранных матчах</dt><dd className="font-medium tabular-nums">{botStats.lossPoints}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Всего матчей с участием бота</dt><dd className="font-medium tabular-nums">{botStats.totalMatches}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Чемпионатов с участием</dt><dd className="font-medium tabular-nums">{botStats.tournamentCount}</dd></div>
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">Нет данных</p>
          )}
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="p-5 animate-pulse"><div className="h-4 bg-muted rounded w-1/2 mb-3" /><div className="h-3 bg-muted rounded w-3/4" /></Card>
          ))}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bots.map(bot => (
            <Card key={bot.id} className="p-5 space-y-3 group cursor-pointer hover:border-primary/30 transition-colors overflow-hidden"
              data-testid={`card-bot-${bot.id}`} onClick={() => openEdit(bot)}>
              <div className="flex items-start justify-between gap-2 min-w-0">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  <span className="text-2xl shrink-0">{bot.avatar}</span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm truncate">{bot.name}</h3>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      <Badge variant="outline" className={`text-xs ${STYLE_MAP[bot.style]?.color || ""}`}>
                        {STYLE_MAP[bot.style]?.label || bot.style}
                      </Badge>
                      {bot.llmProvider === "yandex" && <Badge variant="outline" className="text-xs text-sky-400 border-sky-500/25">Yandex GPT</Badge>}
                      {bot.isJudge && <Badge variant="outline" className="text-xs gap-0.5 text-amber-400 border-amber-500/20"><Scale className="w-2.5 h-2.5" />Судья</Badge>}
                      {bot.isTrainer && <Badge variant="outline" className="text-xs gap-0.5 text-green-400 border-green-500/20"><GraduationCap className="w-2.5 h-2.5" />Тренер</Badge>}
                      {typeof bot.club === "string" && bot.club.trim() !== "" && (
                        <Badge variant="secondary" className="text-xs max-w-[140px] truncate" title={bot.club.trim()}>
                          Клуб · {bot.club.trim()}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                    title="Статистика"
                    onClick={(e) => { e.stopPropagation(); setStatsBot(bot); }}><BarChart3 className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                    title="Редактировать"
                    onClick={(e) => { e.stopPropagation(); openEdit(bot); }}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                    title="Удалить"
                    onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(bot.id); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">{bot.description}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1 border-t border-border/50">
                <span className="flex items-center gap-1"><Trophy className="w-3 h-3 text-primary" />{bot.rating} ELO</span>
                <span className="flex items-center gap-1"><Target className="w-3 h-3 text-green-400" />{bot.wins}W</span>
                <span className="flex items-center gap-1 text-red-400">{bot.losses}L</span>
                <span className="flex items-center gap-1"><Handshake className="w-3 h-3" />{bot.draws}D</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
